import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Original component: https://github.com/livekit/components-js/blob/main/packages/react/src/components/participant/ParticipantTile.tsx
import { isTrackReference, isTrackReferencePinned, } from '@livekit/components-core';
import { AudioTrack, BarVisualizer, FocusToggle, LockLockedIcon, ParticipantContext, ParticipantName, ParticipantPlaceholder, TrackMutedIndicator, TrackRefContext, VideoTrack, useConnectionQualityIndicator, useEnsureTrackRef, useFeatureContext, useIsEncrypted, useMaybeLayoutContext, useMaybeParticipantContext, useMaybeTrackRefContext, useParticipantTile, } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { capitalize } from 'lodash-es';
import { forwardRef, useCallback } from 'react';
import { MicrophoneDisabledIcon, ScreenIcon } from '@marvelapp/ballpark-icons';
import { BtwTooltip } from '../BtwTooltip';
import { Stack } from '../Stack';
/**
 * The `ParticipantContextIfNeeded` component only creates a `ParticipantContext`
 * if there is no `ParticipantContext` already.
 * @example
 * ```tsx
 * <ParticipantContextIfNeeded participant={trackReference.participant}>
 *  ...
 * </ParticipantContextIfNeeded>
 * ```
 * @public
 */
export const ParticipantContextIfNeeded = function ParticipantContextIfNeeded(props) {
    const hasContext = !!useMaybeParticipantContext();
    return props.participant && !hasContext ? (_jsx(ParticipantContext.Provider, { value: props.participant, children: props.children })) : (_jsx(_Fragment, { children: props.children }));
};
/**
 * Only create a `TrackRefContext` if there is no `TrackRefContext` already.
 * @internal
 */
export const TrackRefContextIfNeeded = function TrackRefContextIfNeeded(props) {
    const hasContext = !!useMaybeTrackRefContext();
    return props.trackRef && !hasContext ? (_jsx(TrackRefContext.Provider, { value: props.trackRef, children: props.children })) : (_jsx(_Fragment, { children: props.children }));
};
/**
 * The `ParticipantTile` component is the base utility wrapper for displaying a visual representation of a participant.
 * This component can be used as a child of the `TrackLoop` component or by passing a track reference as property.
 *
 * @example Using the `ParticipantTile` component with a track reference:
 * ```tsx
 * <ParticipantTile trackRef={trackRef} />
 * ```
 * @example Using the `ParticipantTile` component as a child of the `TrackLoop` component:
 * ```tsx
 * <TrackLoop>
 *  <ParticipantTile />
 * </TrackLoop>
 * ```
 * @public
 */
export const ParticipantTile = forwardRef(function ParticipantTile(_a, ref) {
    var _b, _c;
    var { children, disableSpeakingIndicator, onParticipantClick, trackRef, withQualityTooltip = false } = _a, htmlProps = __rest(_a, ["children", "disableSpeakingIndicator", "onParticipantClick", "trackRef", "withQualityTooltip"]);
    const trackReference = useEnsureTrackRef(trackRef);
    const { quality } = useConnectionQualityIndicator({
        participant: trackReference.participant,
    });
    const { elementProps } = useParticipantTile({
        htmlProps,
        disableSpeakingIndicator,
        onParticipantClick,
        trackRef: trackReference,
    });
    const isEncrypted = useIsEncrypted(trackReference.participant);
    const layoutContext = useMaybeLayoutContext();
    const autoManageSubscription = (_b = useFeatureContext()) === null || _b === void 0 ? void 0 : _b.autoSubscription;
    const handleSubscribe = useCallback((subscribed) => {
        if (trackReference.source &&
            !subscribed &&
            layoutContext &&
            layoutContext.pin.dispatch &&
            isTrackReferencePinned(trackReference, layoutContext.pin.state)) {
            layoutContext.pin.dispatch({ msg: 'clear_pin' });
        }
    }, [trackReference, layoutContext]);
    return (_jsx("div", Object.assign({ "data-participant": trackReference.participant.attributes.video_role === 'PARTICIPANT', ref: ref, style: { position: 'relative' } }, elementProps, { children: _jsx(TrackRefContextIfNeeded, { trackRef: trackReference, children: _jsxs(ParticipantContextIfNeeded, { participant: trackReference.participant, children: [children !== null && children !== void 0 ? children : (_jsxs(_Fragment, { children: [isTrackReference(trackReference) &&
                                (((_c = trackReference.publication) === null || _c === void 0 ? void 0 : _c.kind) === 'video' ||
                                    trackReference.source === Track.Source.Camera ||
                                    trackReference.source === Track.Source.ScreenShare) ? (_jsx(VideoTrack, { trackRef: trackReference, onSubscriptionStatusChanged: handleSubscribe, manageSubscription: autoManageSubscription })) : (_jsxs(_Fragment, { children: [isTrackReference(trackReference) && (_jsx(AudioTrack, { trackRef: trackReference, onSubscriptionStatusChanged: handleSubscribe })), _jsx(BarVisualizer, { barCount: 12, options: { minHeight: 1 } })] })), _jsx("div", { className: "lk-participant-placeholder", children: _jsx(ParticipantPlaceholder, {}) }), _jsxs("div", { className: "lk-participant-metadata", children: [_jsx("div", { className: "lk-participant-metadata-item", children: trackReference.source === Track.Source.Camera ||
                                            trackReference.source === Track.Source.Microphone ? (_jsxs(_Fragment, { children: [isEncrypted && (_jsx(LockLockedIcon, { style: { marginRight: '0.25rem' } })), _jsx(TrackMutedIndicator, { trackRef: {
                                                        participant: trackReference.participant,
                                                        source: Track.Source.Microphone,
                                                    }, show: 'muted', children: _jsx(MicrophoneDisabledIcon, { className: "size-3.5 text-white" }) }), _jsx(ParticipantName, {})] })) : (_jsxs(_Fragment, { children: [_jsx(ScreenIcon, { className: "mr-0.5 size-3.5 text-white" }), _jsx(ParticipantName, { children: "'s screen" })] })) }), _jsx(QualityIndicator, { quality: quality, withTooltip: withQualityTooltip })] })] })), _jsx(FocusToggle, { trackRef: trackReference })] }) }) })));
});
const QualityIndicator = function QualityIndicator({ quality, withTooltip, }) {
    const qualityIndicator = (_jsx("div", { className: "lk-connection-quality-container", children: _jsx(Stack, { direction: "row", gap: "0.5", "data-quality": quality, className: "lk-participant-metadata-item lk-connection-quality", children: _jsxs("svg", { viewBox: "0 0 16 12", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "h-3 w-4 shrink-0", children: [_jsx("rect", { y: "8", width: "4", height: "4", rx: "1", fill: "currentColor" }), _jsx("rect", { x: "6", y: "4", width: "4", height: "8", rx: "1", fill: "currentColor" }), _jsx("rect", { x: "12", width: "4", height: "12", rx: "1", fill: "currentColor" })] }) }) }));
    if (withTooltip) {
        return (_jsx(BtwTooltip, { content: `${capitalize(quality)} quality`, collisionPadding: 4, disableHoverableContent: true, children: qualityIndicator }));
    }
    return qualityIndicator;
};
